import {
  ComputedDataKey,
  ExcludedStationRouteIdsVirtualFieldName,
  RideOpenerIdVirtualFieldName,
  VirtualDataKey,
} from "../constants";
import {
  DirectionEnum,
  DirectionsServiceEnum,
  DragTypeStationEnum,
  RebuildRouteTypeEnum,
  RouteRebuildSourceEnum,
  TrafficTypeEnum,
  WaypointCategoryEnum,
} from "../enums";
import { ILocationInfoBase } from "./common.models";
import { DateYMDString, TTimeHHmm } from "./date-time.models";
import {
  ISortableData,
  TDraggingStation,
  TNoDrag,
  TNotDragging,
} from "./rides.drag.models";

export interface IStationRouteWrapperProps {
  stationId: number;
  stationRouteDto: IStationRouteDto;
  stationIndex: number;
  isLastStation: boolean;
  isFirstStation: boolean;
  dragDropStationHandler?: (sortableUpdatedIds: number[]) => void;
  /** When false (filtered list), drag reorder is disabled. */
  isDragReorderAllowed?: boolean;
}

export interface IStationRouteStationInfo {
  id: number;
  name: string;
  locationInfo: ILocationInfoBase;
  isLocked?: boolean;
  comment?: string;
  timeArrival?: TTimeHHmm /* HH:mm format for time*/;
  areaId?: number;
  /** Routing-only point without passengers — not a DB station. */
  waypointCategory?: WaypointCategoryEnum;
}

/**
 * Minimal routing waypoint row contract — id + optional secondaryId + category.
 * Detect waypoints by waypointCategory, not by negative id.
 */
export interface IRoutingWaypointStationRow {
  id: number;
  secondaryId?: number;
  waypointCategory?: WaypointCategoryEnum;
}

/** Partial row accepted by waypoint category detectors. */
export type TRoutingWaypointStationInput = Partial<IRoutingWaypointStationRow>;

/** Minimal station row shape for waypoint insert / map UI (ride, course, station route). */
export interface IStationRouteWaypointStationLike extends IRoutingWaypointStationRow {
  name: string;
  locationInfo: ILocationInfoBase;
  areaId?: number;
  timeArrival?: TTimeHHmm | "";
}

/** Ride metadata fields that identify the active ML path (cache / customized). */
export interface IEffectiveMlRouteIds {
  usedCachedRouteId?: number | null;
  customStationRouteId?: number | null;
}

export interface IResolveRecountedStationsInfoParams<
  T extends IRoutingWaypointStationRow,
> {
  recounted: T[];
  source: T[];
  sourceRouteIds: IEffectiveMlRouteIds;
  recountRouteIds: IEffectiveMlRouteIds;
  shouldAcceptNewCustomizedMlPath?: boolean;
}

export interface IStationRouteAvailability {
  from?: TTimeHHmm;
  to?: TTimeHHmm;
  dayTypeIds?: number[];
  dayGroupIds?: number[];
}

export interface IStationRouteResult {
  orderedStationIds: number[];
  durations: number[];
  totalDuration: number;
  distances: number[];
  totalDistance: number;
  routePolyline?: string;
  /** Persisted geo waypoints (negative synthetic ids, no DB station). */
  waypointStops?: IStationRouteStationInfo[];
}

export interface IStationRouteSaveRouteFields {
  orderedStationIds: number[];
  waypointIds: number[];
  originStationId?: number;
  destinationStationId?: number;
  result: IStationRouteResult | undefined;
}

export interface IStationRouteWaypointLocationDraft {
  fullAddress: string;
  latitude: number;
  longitude: number;
  /** Optional display name written to StationInfo.name on save. */
  customName?: string;
  /** Index in full station list after which the waypoint is inserted. */
  insertAfterIndex?: number;
}

export interface IStationRouteWaypointSegmentOption {
  insertAfterIndex: number;
  fromName: string;
  toName: string;
}

export interface IStationRouteMetadata {
  sourceRideId?: number;
  availability?: IStationRouteAvailability;
  tags?: number[];
  remarks?: string;
  direction?: DirectionEnum;
  provider?: DirectionsServiceEnum;
  /** Last routing engine used (synced with `provider` on write). */
  directionsService?: DirectionsServiceEnum;
  /** Instant the last rebuild was applied in this editor (ISO). */
  routeRebuildAt?: TDateISO;
  traffic?: TrafficTypeEnum;
  routeDate?: TDateISO; // Route start date-time (for traffic calculation)
  baseRideDate?: TDateISO; // Base ride date-time (rideEnd for PickUp, rideStart for DropOff)
  /** When true, cache was saved in osrmFirst flow (OSRM order + Google inner optimize); lookup by base station + set of stations. */
  savedInOsrmFirstFlow?: boolean;
  /** Retention days applied when this row was written (`storeRouteCacheDays` / default). Kept in metadata; not an editable form field. Card UI hides TTL / expiry for customized ML paths; for system cache shows current system TTL and may note this value when it differs. */
  cacheDurationDays?: number;
}

export interface IStationRouteComputedData {
  stationInfos?: IStationRouteStationInfo[];
  /** Computed on read for grid: route includes routing map waypoints between passenger stations. */
  hasWaypoints?: boolean;
}

interface IStationRouteVirtualData {
  excludedStationRouteIds?: number[];
  rideOpenerId?: number;
}

export interface IStationRouteDto {
  id: number;
  companyId?: number;
  originStationName?: string;
  originStationId: number;
  destinationStationId: number;
  destinationStationName?: string;
  userUpdaterId?: number;
  waypointIds: number[];
  result: IStationRouteResult;
  avoidTolls?: boolean;
  optimized: boolean;
  frozen: boolean;
  customized: boolean;
  metadata?: IStationRouteMetadata;
  createdAt: TDateISO;
  updatedAt: TDateISO;
  deletedAt?: TDateISO;
  [ComputedDataKey]?: IStationRouteComputedData;
  [VirtualDataKey]?: IStationRouteVirtualData;
}

export interface IStationRouteUsageStatsDto {
  rideIds: number[];
  /** Distinct production ids from matching rides (`ride.productionId`). */
  productionIds: number[];
  /** Earliest matching ride.createdAt (ISO), if any. */
  ridesCreatedFrom?: string | null;
  /** Latest matching ride.createdAt (ISO), if any. */
  ridesCreatedTo?: string | null;
}

/** Color tone key for statistics tab count labels. */
export type TStationRouteStatisticsLabelTone =
  "rides" | "ridesCreated" | "productions";

export interface IStationRouteProps {
  station: IStationRouteStationInfo;
  stationRouteDto: IStationRouteDto;
  stationIndex: number;
  isLastStation: boolean;
  isFirstStation: boolean;
  isDragClone?: boolean;
  /** When false (filtered list), drag reorder is disabled. */
  isDragReorderAllowed?: boolean;
}

export type TDragDropStationRouteSortable = {
  type: DragTypeStationEnum.STATION;
  sortable: ISortableData<number>;
  payload: IStationRouteProps;
};

export type TDraggingStationRouteStationPayload =
  TNotDragging | TDraggingStation;
export type TDragStationRouteStation = {
  type: DragTypeStationEnum.STATION;
  payload: IStationRouteProps;
};

export type TDragStationRouteData = TNoDrag | TDragStationRouteStation;

export type TDropStationRouteData = {
  stationRouteDrop?: IStationRouteDto;
  accepts?: DragTypeStationEnum[];
  typeDrop: DragTypeStationEnum;
};

export class RecountStationRouteDistancesCriteriaDto {
  stations: IStationRouteStationInfo[];
  optimize = true;
  avoidTolls?: boolean;
  direction?: DirectionEnum;
  provider: DirectionsServiceEnum;
  rebuildType?: RebuildRouteTypeEnum;
  constructor(
    constructorData?: Partial<RecountStationRouteDistancesCriteriaDto>,
  ) {
    Object.assign(this, constructorData);
  }
}

export interface IUpdateStationRouteDistancesCriteria {
  computedData?: IStationRouteComputedData;
  optimize?: boolean;
  direction?: DirectionEnum;
  skipApiCheck?: boolean;
}

export class RecountStationsRouteResultDto {
  stationsInfo: IStationRouteStationInfo[];
  result: IStationRouteResult;
  existingRouteId?: number;
  provider?: DirectionsServiceEnum;
  directionsService?: DirectionsServiceEnum;
  routeRebuildSource?: RouteRebuildSourceEnum;

  constructor(constructorData?: Partial<RecountStationsRouteResultDto>) {
    Object.assign(this, constructorData);
  }
}

export type TStationRouteFieldName =
  | "customized"
  | "frozen"
  | "optimized"
  | "originStationId"
  | "destinationStationId"
  | "waypointIds"
  | "createdAt"
  | "optimized"
  | "metadata.sourceRideId"
  | "metadata.availability"
  | "metadata.remarks"
  | "metadata.direction"
  | "metadata.tags"
  | typeof ExcludedStationRouteIdsVirtualFieldName
  | typeof RideOpenerIdVirtualFieldName;

export const STATION_ROUTE_FORM_EXCLUDED_METADATA_FIELDS: Partial<
  keyof IStationRouteMetadata
>[] = [
  "sourceRideId",
  "direction",
  "provider",
  "directionsService",
  "cacheDurationDays",
];

/** Resolve routing engine from station route metadata (legacy rows may only have `provider`). */
export const resolveStationRouteDirectionsService = (
  metadata?: IStationRouteMetadata | null,
): DirectionsServiceEnum | undefined =>
  metadata?.directionsService ?? metadata?.provider;

/** Apply routing engine to in-memory station route metadata before save. */
export const withStationRouteRoutingEngineMetadata = ({
  metadata,
  engine,
  routeRebuildAt,
}: {
  metadata: IStationRouteMetadata | undefined;
  engine: DirectionsServiceEnum;
  routeRebuildAt?: TDateISO;
}): IStationRouteMetadata => ({
  ...metadata,
  provider: engine,
  directionsService: engine,
  ...(routeRebuildAt ? { routeRebuildAt } : {}),
});

export interface IRouteCacheStatsByDayDto {
  day: string;
  /** Cache hit % across all routing providers (cached / total * 100). */
  routeCachePercent: number;
  getRouteCacheSum: number;
  getRouteTotalSum: number;
  getDistanceCacheSum: number;
  getDistanceNotCachedSum: number;
  getRouteOsrmFirstFlowCallsSum: number;
  getRouteNonOsrmFirstFlowCallsSum: number;
  getRouteTotalCallsSum: number;
}

/** Day and count; used for "Rides created by date" and "Bookings created by date" diagrams. */
export interface ICountByDayDto {
  day: DateYMDString;
  count: number;
}

/** Ride projection for map-stat diagrams; backend returns list for period, frontend aggregates by day. */
export interface IRideStatItemDto {
  id: number;
  companyId: number;
  companyName?: string | null;
  rideStart?: TDateISO | null;
  status: string;
  createdAt: TDateISO;
  stationsCount: number;
  passengersCount: number;
}

/** Booking projection for map-stat diagrams; backend returns list for period, frontend aggregates by day. */
export interface IBookingStatItemDto {
  id: number;
  companyId: number;
  companyName?: string | null;
  passengerId?: number | null;
  bookTime?: TDateISO | null;
  status: string;
  createdAt: TDateISO;
}

/** Count by day and by company for stacked diagram (rides or bookings). */
export interface ICountByDayByCompanyDto {
  day: DateYMDString;
  byCompany: Record<number, number>;
}

/** Count by day and by status for stacked diagram (rides or bookings). */
export interface ICountByDayByStatusDto {
  day: DateYMDString;
  byStatus: Record<string, number>;
}
